import type {
  ApplicationPipelineView,
  ApprovedRepaymentInstalment,
  ApplicationReviewQueuePage,
  ContractOfferDocument,
  PaginatedData,
  RepaymentScheduleEntry,
  SubscriptionApplication,
  EmploymentStatus,
} from '@solar/contracts';

import { apiClient } from '../../lib/api-client';
import { toQuery } from '../../lib/api-query';

export interface SubscriptionListFilters {
  page?: number;
  pageSize?: number;
  status?: string;
  customerId?: string;
}

export const applicationsApi = {
  list(filters: SubscriptionListFilters = {}): Promise<PaginatedData<SubscriptionApplication>> {
    return apiClient.get(`/subscriptions${toQuery({ ...filters })}`);
  },

  listForCustomer(
    filters: Pick<SubscriptionListFilters, 'page' | 'pageSize' | 'status'> = {},
  ): Promise<PaginatedData<SubscriptionApplication>> {
    return apiClient.get(`/customer/applications${toQuery({ ...filters })}`);
  },

  listContractsForCustomer(
    filters: Pick<SubscriptionListFilters, 'page' | 'pageSize'> = {},
  ): Promise<PaginatedData<SubscriptionApplication>> {
    return apiClient.get(`/customer/contracts${toQuery({ ...filters })}`);
  },

  get(id: string): Promise<SubscriptionApplication> {
    return apiClient.get(`/customer/applications/${id}`);
  },

  create(input: {
    customerId: string;
    productId: string;
    planTemplateId: string;
    installationAddressId: string;
    employmentStatus?: EmploymentStatus;
    discountAmount?: string;
    preferredDebitDay?: number;
    plannedFirstDueDate?: string;
  }): Promise<SubscriptionApplication> {
    return apiClient.post('/subscriptions', input);
  },

  createForCustomer(input: {
    productId: string;
    planTemplateId: string;
    installationAddressId: string;
    employmentStatus?: EmploymentStatus;
    preferredDebitDay?: number;
    plannedFirstDueDate?: string;
  }): Promise<SubscriptionApplication> {
    return apiClient.post('/customer/applications', input);
  },

  update(
    id: string,
    input: {
      productId?: string;
      planTemplateId?: string;
      installationAddressId?: string;
      employmentStatus?: EmploymentStatus;
      preferredDebitDay?: number;
      plannedFirstDueDate?: string;
    },
  ): Promise<SubscriptionApplication> {
    return apiClient.patch(`/customer/applications/${id}`, input);
  },

  submit(id: string): Promise<SubscriptionApplication> {
    return apiClient.post(`/customer/applications/${id}/submit`);
  },

  accept(
    id: string,
    signatureDocumentId: string,
    expectedOfferVersion: number,
  ): Promise<SubscriptionApplication> {
    return apiClient.post(`/customer/applications/${id}/accept`, {
      signatureDocumentId,
      termsAccepted: true,
      expectedOfferVersion,
    });
  },

  getContractPreview(id: string): Promise<ContractOfferDocument> {
    return apiClient.get(`/customer/applications/${id}/contract-preview`);
  },

  getSchedule(id: string): Promise<RepaymentScheduleEntry[]> {
    return apiClient.get(`/customer/contracts/${id}/repayment-schedule`);
  },

  listAdmin(
    filters: SubscriptionListFilters = {},
  ): Promise<PaginatedData<SubscriptionApplication>> {
    return apiClient.get(`/admin/subscriptions${toQuery({ ...filters })}`);
  },

  getAdminPipeline(): Promise<ApplicationPipelineView> {
    return apiClient.get('/admin/operational/applications/pipeline');
  },

  getAdminReviewQueue(
    filters: Pick<SubscriptionListFilters, 'page' | 'pageSize'> = {},
  ): Promise<ApplicationReviewQueuePage> {
    return apiClient.get(`/admin/operational/applications/review-queue${toQuery({ ...filters })}`);
  },

  getAdmin(id: string): Promise<SubscriptionApplication> {
    return apiClient.get(`/admin/subscriptions/${id}`);
  },

  setAdminDiscount(id: string, discountAmount: string): Promise<SubscriptionApplication> {
    return apiClient.patch(`/subscriptions/${id}`, { discountAmount });
  },

  startReview(id: string): Promise<SubscriptionApplication> {
    return apiClient.post(`/admin/subscriptions/${id}/review`);
  },

  recoverExpiredOffer(id: string, expectedOfferVersion: number): Promise<SubscriptionApplication> {
    return apiClient.post(`/admin/subscriptions/${id}/expired-offer/review`, {
      expectedOfferVersion,
    });
  },

  previewApprovalSchedule(
    id: string,
    firstRepaymentDate: string,
  ): Promise<ApprovedRepaymentInstalment[]> {
    return apiClient.post(`/admin/subscriptions/${id}/approval-schedule-preview`, {
      firstRepaymentDate,
    });
  },

  approve(
    id: string,
    notes?: string,
    firstRepaymentDate?: string,
  ): Promise<SubscriptionApplication> {
    return apiClient.post(`/admin/subscriptions/${id}/approve`, { notes, firstRepaymentDate });
  },

  reject(id: string, reason: string): Promise<SubscriptionApplication> {
    return apiClient.post(`/admin/subscriptions/${id}/reject`, { reason });
  },

  requestChanges(id: string, reason: string): Promise<SubscriptionApplication> {
    return apiClient.post(`/admin/subscriptions/${id}/request-changes`, { reason });
  },

  generateSchedule(id: string): Promise<RepaymentScheduleEntry[]> {
    return apiClient.post(`/admin/subscriptions/${id}/generate-schedule`);
  },
};
